import React, { useState, useEffect, useRef } from 'react';
import ContactForm from '../ContactForm';
import { ContactSectionProps } from '../../types';
import { typography, layout, colors } from '../../styles/designTokens';

const ContactSection: React.FC<ContactSectionProps> = ({
  onSubmit,
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [isFormContainerVisible, setIsFormContainerVisible] = useState(false);

  // בדיקה אם הטופס גלוי
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsFormContainerVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    const currentFormRef = formContainerRef.current;
    if (currentFormRef) {
      observer.observe(currentFormRef);
    }

    return () => {
      if (currentFormRef) {
        observer.unobserve(currentFormRef);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="contact" 
      className="relative" 
      style={{ 
        background: colors.accentSpecial.buttonGradientDiagonal, // גרדיאנט זהב-כתום כמו כפתור רכישה מהירה
        paddingTop: 'clamp(16px, 3vw, 24px)',
        paddingBottom: 'clamp(16px, 3vw, 24px)',
        overflowX: 'hidden', // מונע overflow אופקי
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <div className="container mx-auto px-4 relative" style={{ maxWidth: layout.contentMaxWidth }}>
        {/* Grid Layout - כותרת בצד שמאל, טופס בצד ימין */}
        <div 
          className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center"
          style={{ 
            direction: 'rtl',
          }}
        >
          {/* Left Side - Headers - כותרת בצד שמאל (בדסקטופ) - באמצע אנכית */}
          <div className="text-center flex flex-col justify-center relative">
            {/* Contact Icon */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'clamp(12px, 2vw, 20px)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: 'clamp(3rem, 6vw, 4.5rem)', height: 'clamp(3rem, 6vw, 4.5rem)', color: '#000000' }} aria-hidden="true">
                <path fillRule="evenodd" d="M19.5 9.75a.75.75 0 0 1-.75.75h-4.5a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 1 1.5 0v2.69l4.72-4.72a.75.75 0 1 1 1.06 1.06L16.06 9h2.69a.75.75 0 0 1 .75.75Z" clipRule="evenodd" />
                <path fillRule="evenodd" d="M1.5 4.5a3 3 0 0 1 3-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 0 1-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 0 0 6.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 0 1 1.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 0 1-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5Z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 
              className="contact-section-title font-black leading-tight"
              style={{ 
                fontSize: 'clamp(2rem, 5vw, 3.2rem)',
                fontFamily: typography.heading.fontFamily,
                letterSpacing: typography.heading.letterSpacing,
                color: '#000000',
                textAlign: 'center',
                whiteSpace: 'pre-line',
              }}
            >
              אנו כאן עבורכם,{'\n'}שמחים לתת שירות
            </h2>
          </div>

          {/* Right Side - Contact Form - טופס בצד ימין (בדסקטופ) - הוזז ימינה */}
          <div style={{ marginRight: 'clamp(20px, 5vw, 60px)', marginLeft: 'auto' }}>
            <div 
              ref={formContainerRef}
              className="w-full contact-form-container"
              style={{
                backgroundColor: '#000000', // רקע שחור לכרטיס
                border: '2px solid #FFFFFF', // מסגרת לבן
                borderRadius: '24px', // border radius כמו FAQ
                padding: 'clamp(20px, 3vw, 32px)', // חזרה לגודל המקורי
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                maxWidth: '100%',
                opacity: isFormContainerVisible ? 1 : 0,
                transform: isFormContainerVisible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)',
                transition: 'opacity 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s',
              }}
            >
              <ContactForm onSubmit={onSubmit} isFormContainerVisible={isFormContainerVisible} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;

